/**
 * The phase band: every phase of the plan, its cycle range, its type, and which one the canvas is
 * showing.
 *
 * From v3's `.phasebar`. Each tab carries a coloured pip on its left edge — the accent on the
 * phase in view, `--deload-blue` on a deload phase, neutral otherwise. That is the view ADR §1
 * colour semantic applied exactly: the accent marks the trainer's own position, and blue is a
 * structural label rather than a state.
 *
 * ── Tabs, and real ones ──────────────────────────────────────────────────────────────────────
 * `role="tablist"` with roving focus: one tab stop for the whole band, arrows to move between
 * phases, Home and End to jump to the ends. A row of nine buttons that each take a tab stop is
 * nine presses to get past the band, and a trainer using the keyboard crosses it on every screen.
 *
 * The tab panel is the week itself, which is why `aria-controls` points at the canvas region. The
 * selection lives in the URL (`?phase=`), so Back leaves the phase rather than the plan and a link
 * carries the phase the trainer was looking at.
 *
 * ── The band draws chips, "+ Faza" and a summary, and nothing else ──────────────────────────
 * The 07.09 programator mock's phase strip carries exactly that, so the settings ⋯ and the delete ×
 * that used to stand beside the strip are gone. **Pressing the tab the canvas is ALREADY showing
 * opens the phase editor**; pressing any other tab still just selects it. Deleting a phase lives
 * inside that editor, where it always also lived — the × was a second entry to it.
 *
 * That keeps the tablist intact, which is not negotiable: a `tablist` may own only tabs, and a
 * control inside each one made the band announce as twice as many items as it holds while arrowing
 * across the tabs stepped onto things that are not tabs (axe: `aria-required-children`). Nothing was
 * added to a tab — the selected one carries `aria-haspopup="dialog"` and `aria-expanded`, which say
 * what a second press does without touching its name or its role, and `PhaseEditor` anchors itself
 * to that button rather than owning a trigger. "+ Faza" still sits in `.bar` beside the strip.
 *
 * Roving focus is untouched: one tab stop for the band, arrows between phases, Home and End to the
 * ends, and selection follows focus. A press is the only gesture that opens the editor, so arrowing
 * onto the selected tab never opens anything.
 *
 * ── The tab is OUTSIDE the popup, so its own press has to be exempted from the dismissal ────
 * Base UI dismisses a popover on a press outside it, and the tab is outside it. Measured before it
 * was fixed: pressing the selected tab a second time closed the popover on `outsidePress` and the
 * same press then re-opened it through the handler below, so `aria-expanded` promised a collapse
 * that never happened. `onOpenChange` therefore ignores an `outsidePress` that landed on the tab
 * itself — the tab owns that press, and its handler is what answers it.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PhaseTabView } from '../model/builderChrome';
import type { PhaseAddMenuView, PhaseEditorView } from '../model/phaseEditorView';

import { PhaseAddMenu, type PhaseAddActions } from './PhaseAddMenu';
import { PhaseEditor, type PhaseEditorActions } from './PhaseEditor';
import styles from './PhaseBar.module.css';

interface PhaseBarProps {
  readonly phases: readonly PhaseTabView[];
  /** "12 tygodni · 3 fazy". */
  readonly total: string;
  readonly onSelect: (index: number) => void;
  /** The id of the region the tabs control — the week canvas. */
  readonly panelId: string;
  /** The "+ Faza" menu's words. `null` on a plan that cannot be edited — nothing is drawn. */
  readonly phaseAdd: PhaseAddMenuView | null;
  readonly add: PhaseAddActions | null;
  /** The settings popover's words for the phase in view. `null` when it cannot be edited. */
  readonly phaseEditor: PhaseEditorView | null;
  readonly edit: PhaseEditorActions | null;
  /**
   * Changes when the band is owed focus — after a confirmed phase deletion, whose ⋯ no longer edits
   * the phase it was opened on. A counter rather than a boolean: deleting two phases in a row has to
   * move focus both times.
   */
  readonly focusSignal: number;
}

/**
 * `string | undefined` rather than `string`, because Vite types a CSS module as an index signature
 * and `noUncheckedIndexedAccess` is on — so every `styles.x` is possibly undefined. `className`
 * accepts that; the map has to admit it too.
 */
const PIP_CLASS: Record<PhaseTabView['pip'], string | undefined> = {
  accent: styles.pipAccent,
  deload: styles.pipDeload,
  neutral: styles.pipNeutral,
};

export function PhaseBar({
  phases,
  total,
  onSelect,
  panelId,
  phaseAdd,
  add,
  phaseEditor,
  edit,
  focusSignal,
}: PhaseBarProps) {
  const { t } = useTranslation();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  // The editor has no trigger of its own — see the header. The band holds the flag, and the ACTIVE
  // tab is both the anchor Base UI positions against and where focus returns when the panel closes.
  const [editorOpen, setEditorOpen] = useState(false);

  const active = phases.findIndex((phase) => phase.isActive);
  // Both halves or neither: the words and the writes travel together, and a plan that cannot be
  // edited draws no editor and makes no promise on its selected tab.
  const canEdit = phaseEditor !== null && edit !== null;

  // A plan can hold more phases than the band can show, and the selected one is not necessarily
  // among the first: `?phase=8` in a bookmarked URL opens a tab that is scrolled past the right
  // edge, with nothing on screen indicating which phase the canvas below is showing. Arrowing
  // between tabs already scrolls, because `focus()` does — this is the case where nothing is
  // focused at all.
  useEffect(() => {
    // jsdom implements no `scrollIntoView`. Guarding rather than stubbing keeps the absence a
    // property of the environment instead of something a test has to know about.
    const tab = tabs.current[active];
    if (tab == null || typeof tab.scrollIntoView !== 'function') return;
    // `nearest` on BOTH axes. The band scrolls horizontally, but a bare `inline` leaves `block`
    // at its `start` default, which asks every scrollable ancestor to align the tab to its top —
    // work with no purpose on a page whose chrome does not scroll at all.
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  // Focus back onto the band after a confirmed phase deletion. The ⋯ that raised the confirm was
  // editing the phase that is now gone, so Base UI's own restore would hand focus to a control that
  // has silently become about a different block — and the band is the thing that changed. `0` is the
  // initial value and never means "take focus", or the band would grab it on every mount.
  useEffect(() => {
    if (focusSignal === 0) return;
    tabs.current[Math.max(0, active)]?.focus();
    // `active` is deliberately absent: this fires when the SIGNAL moves, and re-running it every
    // time the trainer switched tabs would steal focus from whatever they were using.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal]);

  const move = (from: number, to: number) => {
    const target = Math.min(Math.max(0, to), phases.length - 1);
    const phase = phases[target];
    if (phase === undefined || target === from) return;
    onSelect(phase.index);
    // Focus follows selection, which is what "automatic activation" means for a tablist and what
    // makes arrowing through phases feel like one control rather than a series of them.
    tabs.current[target]?.focus();
  };

  return (
    <div className={styles.bar}>
      {/* No wrapper and no fade: the mock draws the tablist directly in the bar, and nothing the
       * mock does not draw may be visible. The band still scrolls when a plan carries more phases
       * than the row can hold, and the selected tab is still scrolled into view below. */}
      <div className={styles.tabs} role="tablist" aria-label={t('web:planBuilder.phaseTabs')}>
        {phases.map((phase, position) => (
          <button
            key={phase.index}
            ref={(node) => {
              tabs.current[position] = node;
            }}
            type="button"
            role="tab"
            id={`phase-tab-${String(phase.index)}`}
            aria-selected={phase.isActive}
            aria-controls={panelId}
            // The roving tab stop: only the selected tab is reachable by Tab; the rest by arrow.
            tabIndex={phase.isActive ? 0 : -1}
            /* What a SECOND press on the tab already selected does. Only the selected tab makes
             * the promise, because only it can keep it — and `title` carries the same words on
             * hover, so the affordance is not announced-only. The tab's NAME is untouched: it is
             * the phase, and a control whose name changed with its selection would be a different
             * control to anyone navigating by name. */
            aria-haspopup={phase.isActive && canEdit ? 'dialog' : undefined}
            aria-expanded={phase.isActive && canEdit ? editorOpen : undefined}
            title={phase.isActive && canEdit ? phaseEditor.triggerLabel : undefined}
            className={`${String(styles.tab)} ${phase.isActive ? String(styles.tabActive) : ''}`}
            onClick={() => {
              // One gesture, two meanings decided by WHERE it lands: a press on another phase
              // moves the canvas, a press on the one already open asks about it. They can never
              // be the same tab, so neither has to guess.
              if (phase.isActive) {
                if (canEdit) setEditorOpen((open) => !open);
              } else onSelect(phase.index);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') move(position, position + 1);
              else if (event.key === 'ArrowLeft') move(position, position - 1);
              else if (event.key === 'Home') move(position, 0);
              else if (event.key === 'End') move(position, phases.length - 1);
              else return;
              event.preventDefault();
            }}
          >
            <span className={`${styles.pip} ${PIP_CLASS[phase.pip]}`} aria-hidden="true" />
            <span className={styles.name}>{phase.name}</span>
            <span className={styles.range}>{phase.range}</span>
            <span className={styles.tag}>{phase.tag}</span>
          </button>
        ))}
      </div>

      {/* The canon's ghost, and it ACTS now.
       *
       * It was drawn permanently inert with the sentence "Dodawanie faz nie jest jeszcze gotowe w
       * tym edytorze" beside it, because there was no phase operation to dispatch. There is one, so
       * the button is a menu trigger and the sentence is gone — a standing explanation for a
       * control that works is noise, and the refusals it CAN have (the phase cap, the plan-wide
       * cycle ceiling) belong on the entries they refuse, where the trainer is when they meet them.
       *
       * ── Both of these sit OUTSIDE the tablist, and that is load-bearing ─────────────────
       * A `tablist` may own only tabs. Both were inside it once, so the band announced as "tab
       * list, 4 items" holding three tabs, an ordinary button and a stray span — the button had no
       * position in the set, and arrowing across the tabs skipped a thing announced as belonging to
       * them (axe: `aria-required-children`). `.bar` is the same flex row, so nothing moved. */}
      {phaseAdd !== null && add !== null && <PhaseAddMenu view={phaseAdd} actions={add} />}
      {canEdit && (
        <PhaseEditor
          view={phaseEditor}
          actions={edit}
          open={editorOpen}
          onOpenChange={(open, details) => {
            // The selected tab is the opener AND is outside the popup, so Base UI reads a press on
            // it as a dismissal. That press belongs to the tab: swallowing it here is what makes the
            // toggle below the single answer to it. See the header for the measurement.
            const target = details.event.target;
            if (
              !open &&
              details.reason === 'outside-press' &&
              target instanceof Node &&
              tabs.current[active]?.contains(target) === true
            ) {
              return;
            }
            setEditorOpen(open);
          }}
          onClose={() => {
            setEditorOpen(false);
          }}
          /* Resolved at open time rather than held in a ref: no tab unmounts when the selection
           * moves, so a ref would still be pointing at whichever tab was selected when it was last
           * filled. See `PhaseEditorProps.anchor`. */
          anchor={() => tabs.current[active] ?? null}
        />
      )}

      {/* The mock's own `<span style="flex:1">`, which is what pushes the total to the right edge. */}
      <span className={styles.spacer} />

      {/* The cycle length on the tape, the plan's span on the grid — and on the tape it is the
       * CONSEQUENCE of every edit the trainer makes: a stepper press, a reorder, an added
       * training all land here and nowhere else. It changed silently. `polite` rather than
       * routing it through the canvas's move announcer, because it is a property of the phase
       * rather than the outcome of one act, and because the two would then describe the same
       * reorder twice. It changes at most once per interaction, so the queue stays short. */}
      <span className={styles.total} aria-live="polite">
        {total}
      </span>
    </div>
  );
}
